import { useEffect, useRef } from "react";

import { analyticsService } from "../../../lib/analytics";
import type { EventTypes } from "../../../lib/analytics/types";

type HomeScreenViewedArgs = EventTypes["HomeScreenViewed"] & {
  /** Blocks the event until the screen's data has resolved. */
  loading: boolean;
};

// Fires HomeScreenViewed once per distinct `variant` per mount, the first time
// the screen finishes loading for that arm. Re-arms when `variant` changes so
// a control user who briefly saw the default (`legacy`) during a readiness
// window still emits a correctly-tagged experiment event — treatment already
// remounts (`HomeContent` ↔ `LegacyHomeContent`) and got a fresh ref for free.
export function useHomeScreenViewedEvent({
  loading,
  variant,
  casesShownCount,
  actionableCasesCount,
  unreadCasesCount,
  hasClosedWin,
}: HomeScreenViewedArgs): void {
  // Set, not a single slot: a readiness window can move the variant back and
  // forth, and re-emitting one is a duplicate exposure on the assignment key.
  const firedVariants = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (loading || firedVariants.current.has(variant)) {
      return;
    }
    firedVariants.current.add(variant);
    analyticsService.trackEvent("HomeScreenViewed", {
      variant,
      casesShownCount,
      actionableCasesCount,
      unreadCasesCount,
      hasClosedWin,
    });
  }, [
    loading,
    variant,
    casesShownCount,
    actionableCasesCount,
    unreadCasesCount,
    hasClosedWin,
  ]);
}
